import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setStoredToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { trackEvent } from "@/lib/analytics";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/");
      return;
    }
    const sid = m[1];
    setSessionId(sid);
    // Check if user already consented before (stored in sessionStorage during login click? we just always show)
    setNeedsConsent(true);
  }, [navigate]);

  const submit = async () => {
    if (!consent) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/session", {
        session_id: sessionId,
        privacy_consent: true,
        marketing_consent: marketingConsent,
      });
      // Safari/iOS ITP fallback: persist token to localStorage so that subsequent
      // requests can use Authorization: Bearer when third-party cookies are blocked.
      if (data?.session_token) {
        setStoredToken(data.session_token);
      }
      // Fire GA4 conversion event only for NEW users (first Google login).
      // Used by Google Ads Smart Bidding to optimize campaigns for real signups.
      if (data?.is_new_user) {
        trackEvent("sign_up", { method: "google", marketing_consent: !!marketingConsent });
      }
      // Clean URL fragment
      window.history.replaceState(null, "", "/");
      await refresh();
      const returnTo = sessionStorage.getItem("return_to") || "/area-personale";
      sessionStorage.removeItem("return_to");
      navigate(returnTo);
    } catch (e) {
      setSubmitting(false);
      navigate("/?auth_error=1");
    }
  };

  if (!needsConsent) {
    return <div className="max-w-3xl mx-auto px-4 py-20 font-ui text-[#5C6B64]">Autenticazione in corso…</div>;
  }

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-16">
      <div className="card-surface p-8">
        <p className="label-eyebrow text-[#C05C45]">Ultimo passo</p>
        <h1 className="mt-2 font-display text-3xl text-[#1A362D] tracking-tight">Conferma l'accesso a Per manina</h1>
        <p className="mt-3 font-ui text-sm text-[#5C6B64] leading-relaxed">
          Raccogliamo esclusivamente <strong>email e nome</strong> del tuo account Google per permetterti di lasciare esperienze e gestire il tuo profilo. <strong>Non profiliamo gli utenti</strong> e <strong>non condividiamo i tuoi dati personali con terzi a fini commerciali</strong>.
        </p>

        <label className="mt-6 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="privacy-policy-checkbox"
            className="mt-1 w-4 h-4 accent-[#C05C45]"
          />
          <span className="font-ui text-sm text-[#1A362D] leading-relaxed">
            Accetto la <a href="/info/privacy" target="_blank" rel="noreferrer" className="underline text-[#C05C45]">Privacy Policy</a> e il trattamento di email e nome per identificarmi quando lascio esperienze e gestire il mio account. <span className="text-[#8A9691]">(obbligatorio)</span>
          </span>
        </label>

        <div className="mt-4 pt-4 border-t border-[#E8E6E1]">
          <p className="font-ui text-xs uppercase tracking-wider text-[#8A9691] mb-2">Opzionale</p>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              data-testid="marketing-consent-checkbox"
              className="mt-1 w-4 h-4 accent-[#C05C45]"
            />
            <span className="font-ui text-sm text-[#1A362D] leading-relaxed">
              Vorrei ricevere occasionalmente <strong>newsletter con comunicazioni alla community od offerte promozionali</strong> solo per prodotti e/o servizi che possono realmente essermi utili in quanto genitore e/o professionista in ambito formativo. Posso disiscrivermi in ogni momento dall'Area Personale.
            </span>
          </label>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={submit}
            disabled={!consent || submitting}
            data-testid="consent-submit-btn"
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Conferma…" : "Conferma e accedi"}
          </button>
          <button onClick={() => navigate("/")} data-testid="consent-cancel-btn" className="btn-secondary">Annulla</button>
        </div>
      </div>
    </div>
  );
}
